import React, { useState, useEffect, useCallback } from "react";
import {
  Tag, Button, Input, Space, Dropdown, Modal, message, Image, Flex,
  Form, InputNumber, Select, Typography, Tooltip, Switch, Upload, Row, Col,
} from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined, CopyOutlined, MoreOutlined, CheckCircleOutlined, CloseCircleOutlined, StopOutlined, EyeOutlined, FileTextOutlined, UploadOutlined, DollarOutlined, EditOutlined, PaperClipOutlined, MailOutlined, DownloadOutlined, BlockOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import * as actions from "../../store/actions/AuthActions";
import FilterBar from 'components/Common/FilterBar';
import PageActions from 'components/Common/PageActions';
import useUrlFilters from 'hooks/useUrlFilters';
import axios from "axios";
import { formatMoney, currencyPrefix, currencyDecimals, recordCurrency } from 'utils/money';
import { copyText } from 'utils/clipboard';

const { Text } = Typography;

// First message out of a DRF error body ({detail}, {field: [msg]}, or [msg]).
const apiErrorMessage = (e, fallback) => {
  const data = e?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return fallback;
  if (data.detail) return data.detail;
  if (data.error) return data.error;
  const first = Array.isArray(data) ? data[0] : Object.values(data)[0];
  return (Array.isArray(first) ? first[0] : first) || fallback;
};

const statusColors = {
  ACTIVE: "blue",
  PAID: "green",
  EXPIRED: "red",
  CANCELLED: "default",
};

// What the status *should read as*. The backend now derives EXPIRED on read, but
// nothing ever stores it, so keep deriving it here too — an older API build (or a
// row fetched before the link lapsed) would otherwise still say ACTIVE.
const getEffectiveStatus = (record) => {
  if (
    record?.status === "ACTIVE" &&
    record?.expires_at &&
    dayjs(record.expires_at).isBefore(dayjs())
  ) {
    return "EXPIRED";
  }
  return record?.status;
};

// The raw stored status, for gating actions the backend still allows on an
// expired link (approve/cancel/attach). Falls back to `status` on older builds.
const getDbStatus = (record) => record?.db_status ?? record?.status;

// Search and the page live in the URL so the view can be shared as a link.
const URL_FILTERS = {
  search: { type: "string" },
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 50 },
};

const PaymentLinksTable = () => {
  const navigate = useNavigate();
  // Table state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const search = filters.search || "";
  // The box searches as you type; it keeps its own text so the cursor does not
  // jump, and resyncs when the URL changes underneath it (back/forward).
  const [searchLive, setSearchLive] = useState(search);
  useEffect(() => setSearchLive(search), [search]);
  const currentPage = filters.page;
  const setCurrentPage = (value) => setFilters({ page: value });
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = (value) => setFilters({ pageSize: value });
  const [total, setTotal] = useState(0);

  // Create modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [createForm] = Form.useForm();
  const [currencies, setCurrencies] = useState([]);
  const createCurrencyId = Form.useWatch('currency', createForm);
  const createCurrencyIso =
    currencies.find((c) => c.id === createCurrencyId)?.iso_code || 'INR';
  // Values to seed the create form with (set when cloning an existing link).
  const [createPrefill, setCreatePrefill] = useState(null);
  // Controlled fileList for the banner Upload, so a cloned banner shows a thumbnail.
  const [createBannerFileList, setCreateBannerFileList] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [smtpOptions, setSmtpOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);

  // Edit modal state (only for ACTIVE + not-expired links)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editForm] = Form.useForm();

  const createSendEmailOn = Form.useWatch('send_confirmation_email', createForm);
  const editSendEmailOn = Form.useWatch('send_confirmation_email', editForm);

  // Review modal state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewingLink, setReviewingLink] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);

  // View details modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLink, setDetailLink] = useState(null);

  // Attach existing payment modal state
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachingLink, setAttachingLink] = useState(null);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [paymentSearchLoading, setPaymentSearchLoading] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  // Confirm action modal state (code confirmation like daily updates)
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmCode, setConfirmCode] = useState("");
  const [userInputCode, setUserInputCode] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingProof, setPendingProof] = useState(null);
  const [pendingProofLink, setPendingProofLink] = useState(null);

  const fetchPaymentLinks = useCallback(async (page = 1, size = 50, searchVal = "") => {
    setLoading(true);
    try {
      const response = await actions.loadPaymentLinks(searchVal, page, size);
      if (response.status === 200) {
        setData(response.data.results || []);
        setTotal(response.data.count || 0);
      }
    } catch (e) {
      message.error("Failed to load payment links");
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPaymentLinks(currentPage, pageSize, search);
  }, [currentPage, pageSize, search]);

  const handleSearch = useCallback((value) => {
    setSearchLive(value);
    setFilters({ search: value, page: 1 });
  }, [setFilters]);

  // Load accounts for create modal
  const loadAccounts = useCallback(async (searchVal = "") => {
    try {
      const response = await actions.loadAllAccounts(searchVal, 1, 20);
      if (response.status === 200) {
        setAccountOptions((response.data.results || response.data || []).map(a => ({
          label: a.name,
          value: a.id,
        })));
      }
    } catch (e) {
      console.error("Error loading accounts:", e);
    }
  }, []);

  // Load payment products for create modal
  const loadPaymentProducts = useCallback(async () => {
    try {
      const response = await actions.loadPaymentProducts();
      if (response.status === 200) {
        setProductOptions((response.data.results || response.data || []).map(p => ({
          label: p.code ? `${p.name} (${p.code})` : p.name,
          value: p.id,
        })));
      }
    } catch (e) {
      console.error("Error loading payment products:", e);
    }
  }, []);

  // Load SMTP configs and email templates for confirmation-email pickers
  const loadConfirmationOptions = useCallback(async () => {
    try {
      const [smtpRes, tplRes] = await Promise.all([
        actions.loadOrgSMTPConfigs(),
        actions.loadEmailTemplates(1),
      ]);
      if (smtpRes?.status === 200) {
        const rows = smtpRes.data.results || smtpRes.data || [];
        setSmtpOptions(rows.map(s => ({ label: s.unique_name, value: s.id })));
      }
      if (tplRes?.status === 200) {
        const rows = tplRes.data.results || tplRes.data || [];
        setTemplateOptions(rows.map(t => ({ label: t.name, value: t.id })));
      }
    } catch (e) {
      console.error("Error loading SMTP/template options:", e);
    }
  }, []);

  // Open the create modal, optionally seeded with values (used by Clone).
  // Same seeding pattern as openEditModal: set the values on the form instance
  // before opening. The banner file arrives later, from prefillBannerFromUrl.
  // Returns the list so callers can pick a default right after loading.
  const loadCurrencies = useCallback(async () => {
    try {
      const response = await axios.get("/masters/currencies/");
      const list = response.data?.results || response.data || [];
      setCurrencies(list);
      return list;
    } catch (e) {
      console.error("Error loading currencies:", e);
      return [];
    }
  }, []);

  const openCreateModal = async (prefill = null, seedOptions = null) => {
    setCreateResult(null);
    setCreatePrefill(prefill);
    setCreateBannerFileList([]);
    createForm.resetFields();
    if (prefill) createForm.setFieldsValue(prefill);
    setCreateModalOpen(true);

    const [, , , currencyResult] = await Promise.allSettled([
      loadAccounts(),
      loadPaymentProducts(),
      loadConfirmationOptions(),
      loadCurrencies(),
    ]);
    if (!createForm.getFieldValue('currency')) {
      const list = currencyResult?.value || [];
      const inr = list.find((c) => c.iso_code === 'INR');
      if (inr) createForm.setFieldsValue({ currency: inr.id });
    }

    // A cloned link's account/product may not be in the first page of options,
    // which would leave the Select showing a raw id. Seed them once the option
    // lists have loaded, so this isn't overwritten by the fetches above.
    if (seedOptions?.account?.value) {
      setAccountOptions((prev) => (
        prev.some((o) => o.value === seedOptions.account.value)
          ? prev
          : [seedOptions.account, ...prev]
      ));
    }
    if (seedOptions?.product?.value) {
      setProductOptions((prev) => (
        prev.some((o) => o.value === seedOptions.product.value)
          ? prev
          : [seedOptions.product, ...prev]
      ));
    }
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setCreateResult(null);
    setCreatePrefill(null);
    setCreateBannerFileList([]);
  };

  // Pull an existing banner image back down as a File so the clone uploads its
  // own copy (the API only accepts a file, not a URL).
  const prefillBannerFromUrl = async (url) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const name = decodeURIComponent(url.split('/').pop().split('?')[0]) || 'banner.png';
      const file = new File([blob], name, { type: blob.type || 'image/png' });
      createForm.setFieldsValue({ banner_image: file });
      setCreateBannerFileList([{ uid: '-1', name, status: 'done', originFileObj: file, url }]);
    } catch (e) {
      message.info('Banner image could not be copied — please re-upload it.');
      console.error('Failed to copy banner image:', e);
    }
  };

  // Clone: open the create modal pre-filled from an existing link. Everything the
  // backend assigns per link (token, expiry, status, payment/invoice, manual
  // payment data) is deliberately left out.
  const handleCloneLink = async (record) => {
    const prefill = {
      amount: record.amount ? parseFloat(record.amount) : null,
      currency: record.currency || undefined,
      description: record.description || '',
      account: record.account || undefined,
      product: record.product || undefined,
      external_reference_id: record.external_reference_id || '',
      customer_name: record.customer_name || '',
      customer_email: record.customer_email || '',
      customer_phone: record.customer_phone || '',
      redirect_url: record.redirect_url || '',
      is_manual_payment_allowed: !!record.is_manual_payment_allowed,
      multiple_payments: !!record.multiple_payments,
      send_confirmation_email: !!record.send_confirmation_email,
      confirmation_smtp_config: record.confirmation_smtp_config || undefined,
      confirmation_email_template: record.confirmation_email_template || undefined,
    };
    const seedOptions = {
      account: record.account && record.account_name
        ? { label: record.account_name, value: record.account }
        : null,
      product: record.product && record.product_name
        ? { label: record.product_name, value: record.product }
        : null,
    };

    // Open first (it resets the form), then copy the banner in — otherwise the
    // reset could land after the fetch resolves and wipe the banner field.
    await openCreateModal(prefill, seedOptions);
    if (record.banner_image) {
      await prefillBannerFromUrl(record.banner_image);
    }
  };

  // Create payment link
  const handleCreatePaymentLink = async (values) => {
    setCreateLoading(true);
    try {
      const fd = new FormData();
      fd.append('amount', values.amount);
      if (values.currency) fd.append('currency', values.currency);
      fd.append('description', values.description || '');
      if (values.account) fd.append('account', values.account);
      fd.append('customer_name', values.customer_name || '');
      fd.append('customer_email', values.customer_email || '');
      fd.append('customer_phone', values.customer_phone || '');
      fd.append('redirect_url', values.redirect_url || '');
      if (values.product) fd.append('product', values.product);
      if (values.external_reference_id) fd.append('external_reference_id', values.external_reference_id);
      fd.append('is_manual_payment_allowed', values.is_manual_payment_allowed ? 'true' : 'false');
      fd.append('multiple_payments', values.multiple_payments ? 'true' : 'false');
      fd.append('send_confirmation_email', values.send_confirmation_email ? 'true' : 'false');
      if (values.confirmation_smtp_config) fd.append('confirmation_smtp_config', values.confirmation_smtp_config);
      if (values.confirmation_email_template) fd.append('confirmation_email_template', values.confirmation_email_template);
      if (values.banner_image) fd.append('banner_image', values.banner_image);

      const response = await actions.createPaymentLink(fd);
      if (response.status === 201) {
        setCreateResult(response.data);
        message.success("Payment link created!");
        fetchPaymentLinks(currentPage, pageSize, search);
      }
    } catch (e) {
      message.error(apiErrorMessage(e, "Failed to create payment link"));
      console.error(e);
    }
    setCreateLoading(false);
  };

  // Open edit modal for an active, unexpired link.
  // All fields are shown; only the editable five accept input — the rest are disabled.
  const openEditModal = (record) => {
    setEditingLink(record);
    loadAccounts();
    loadPaymentProducts();
    loadConfirmationOptions();
    editForm.setFieldsValue({
      amount: record.amount ? parseFloat(record.amount) : null,
      description: record.description || '',
      account: record.account || undefined,
      product: record.product || undefined,
      external_reference_id: record.external_reference_id || '',
      customer_name: record.customer_name || '',
      customer_email: record.customer_email || '',
      customer_phone: record.customer_phone || '',
      redirect_url: record.redirect_url || '',
      is_manual_payment_allowed: !!record.is_manual_payment_allowed,
      multiple_payments: !!record.multiple_payments,
      send_confirmation_email: !!record.send_confirmation_email,
      confirmation_smtp_config: record.confirmation_smtp_config || undefined,
      confirmation_email_template: record.confirmation_email_template || undefined,
    });
    setEditModalOpen(true);
  };

  const handleEditPaymentLink = async (values) => {
    if (!editingLink) return;
    setEditLoading(true);
    try {
      // Multipart so a newly chosen banner image can be uploaded alongside the
      // editable text fields.
      const fd = new FormData();
      fd.append('description', values.description || '');
      fd.append('redirect_url', values.redirect_url || '');
      fd.append('is_manual_payment_allowed', values.is_manual_payment_allowed ? 'true' : 'false');
      if (values.banner_image) fd.append('banner_image', values.banner_image);

      const response = await actions.updatePaymentLink(editingLink.id, fd);
      if (response.status === 200) {
        message.success("Payment link updated");
        setEditModalOpen(false);
        setEditingLink(null);
        fetchPaymentLinks(currentPage, pageSize, search);
      }
    } catch (e) {
      const detail = e?.response?.data;
      const msg = detail?.detail
        || (Array.isArray(detail?.non_field_errors) && detail.non_field_errors[0])
        || (typeof detail === 'string' && detail)
        || "Failed to update payment link";
      message.error(msg);
      console.error(e);
    }
    setEditLoading(false);
  };

  const isEditable = (record) => {
    if (getDbStatus(record) !== "ACTIVE") return false;
    if (record?.expires_at && dayjs(record.expires_at).isBefore(dayjs())) return false;
    return true;
  };

  const handleCopyLink = (url) => {
    if (url) {
      copyText(url).then((ok) => {
        if (!ok) return message.error("Couldn't copy. Please copy the link manually.");
        message.success("Payment link copied to clipboard!");
      });
    }
  };

  // Open the details modal and re-fetch fresh data so it reflects edits made
  // elsewhere (e.g. payer details changed on the Payment form).
  const openDetailModal = async (record) => {
    setDetailLink(record);
    setDetailModalOpen(true);
    try {
      const fresh = await actions.loadPaymentLinks(search, currentPage, pageSize);
      if (fresh?.status === 200) {
        setData(fresh.data.results || []);
        setTotal(fresh.data.count || 0);
        const updated = (fresh.data.results || []).find((r) => r.id === record.id);
        if (updated) setDetailLink(updated);
      }
    } catch (_) {
      /* keep the record we already have */
    }
  };

  // Copy the customer-details collection link (shown when no account is attached)
  const handleCopyCustomerDetailsLink = (url) => {
    if (url) {
      copyText(url).then((ok) => {
        if (!ok) return message.error("Couldn't copy. Please copy the link manually.");
        message.success("Customer details link copied to clipboard!");
      });
    } else {
      message.info("No customer details link available for this link");
    }
  };

  // Export the submitted payments of a payment link to a CSV file
  const handleExportSubmittedPayments = (link) => {
    const rows = link?.proofs_summary || [];
    if (rows.length === 0) {
      message.info("No submitted payments to export");
      return;
    }
    const headers = [
      "Receipt", "Customer Name", "Email", "Phone", "UTR / Ref",
      "Submitted At", "Payment Date", "Amount Received", "Payment Mode", "Status",
    ];
    const escape = (val) => {
      const s = val == null ? "" : String(val);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const lines = rows.map((p) => [
      p.payment_receipt_no || p.payment || "",
      p.customer_name || "",
      p.customer_email || "",
      p.customer_phone || "",
      p.reference || "",
      p.submitted_at ? dayjs(p.submitted_at).format("DD MMM YYYY, hh:mm A") : "",
      p.payment_date ? dayjs(p.payment_date).format("DD MMM YYYY") : "",
      p.amount != null ? p.amount : "",
      p.payment_mode || "",
      p.status || "",
    ].map(escape).join(","));
    const csv = [headers.map(escape).join(","), ...lines].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `submitted-payments-${link?.payment_receipt_no || link?.id || "export"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Cancel payment link
  const handleCancel = async (record) => {
    try {
      const response = await actions.cancelPaymentLink(record.id);
      if (response.status === 200) {
        message.success("Payment link cancelled");
        fetchPaymentLinks(currentPage, pageSize, search);
      }
    } catch (e) {
      message.error("Failed to cancel payment link");
      console.error(e);
    }
  };

  // Review manual payment
  const openReviewModal = (record) => {
    setReviewingLink(record);
    setReviewNotes("");
    setReviewModalOpen(true);
  };

  const generateConfirmCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 5; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    return code;
  };

  const initiateReviewAction = (actionType) => {
    setPendingAction(actionType);
    const code = generateConfirmCode();
    setConfirmCode(code);
    setUserInputCode("");
    setConfirmModalVisible(true);
  };

  const handleConfirmReviewAction = useCallback(async () => {
    if (!reviewingLink || !pendingAction) return;
    setReviewLoading(true);
    try {
      const response = await actions.confirmPaymentLinkManualPayment(
        reviewingLink.id,
        pendingAction,
        reviewNotes
      );
      if (response.status === 200) {
        message.success(pendingAction === 'approve' ? 'Payment approved successfully. Status changed to PAID.' : 'Payment rejected.');
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to process review action.');
      console.error(e);
    } finally {
      setConfirmModalVisible(false);
      setReviewModalOpen(false);
      setReviewingLink(null);
      setReviewNotes("");
      setPendingAction(null);
      setReviewLoading(false);
      fetchPaymentLinks(currentPage, pageSize, search);
    }
  }, [reviewingLink, pendingAction, reviewNotes, currentPage, pageSize, search, fetchPaymentLinks]);

  // Direct approve (non-manual) payment link
  const handleApprovePayment = (record) => {
    setPendingAction('direct_approve');
    setReviewingLink(record);
    setReviewNotes("");
    const code = generateConfirmCode();
    setConfirmCode(code);
    setUserInputCode("");
    setConfirmModalVisible(true);
  };

  const handleConfirmDirectApprove = useCallback(async () => {
    if (!reviewingLink) return;
    setReviewLoading(true);
    try {
      const response = await actions.approvePaymentLink(reviewingLink.id, reviewNotes);
      if (response.status === 200) {
        message.success('Payment approved successfully. Status changed to PAID.');
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to approve payment.');
      console.error(e);
    } finally {
      setConfirmModalVisible(false);
      setReviewingLink(null);
      setReviewNotes("");
      setPendingAction(null);
      setReviewLoading(false);
      fetchPaymentLinks(currentPage, pageSize, search);
    }
  }, [reviewingLink, reviewNotes, currentPage, pageSize, search, fetchPaymentLinks]);

  // Multi-payment proof approve/reject (per-proof)
  const initiateProofAction = (link, proof, actionType) => {
    setPendingProofLink(link);
    setPendingProof(proof);
    setReviewNotes("");
    setPendingAction(actionType === 'approve' ? 'proof_approve' : 'proof_reject');
    const code = generateConfirmCode();
    setConfirmCode(code);
    setUserInputCode("");
    setConfirmModalVisible(true);
  };

  const handleConfirmProofAction = useCallback(async () => {
    if (!pendingProofLink || !pendingProof || !pendingAction) return;
    setReviewLoading(true);
    const action = pendingAction === 'proof_approve' ? 'approve' : 'reject';
    try {
      const response = await actions.confirmPaymentLinkProof(
        pendingProofLink.id,
        pendingProof.id,
        action,
        reviewNotes
      );
      if (response.status === 200) {
        message.success(action === 'approve' ? 'Payment approved.' : 'Payment rejected.');
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to process proof.');
      console.error(e);
    } finally {
      setConfirmModalVisible(false);
      setPendingProofLink(null);
      setPendingProof(null);
      setPendingAction(null);
      setReviewNotes("");
      setReviewLoading(false);
      // Refresh both list and currently-open detail (so the proof list updates without reopening)
      try {
        const fresh = await actions.loadPaymentLinks(search, currentPage, pageSize);
        if (fresh?.status === 200) {
          setData(fresh.data.results || []);
          setTotal(fresh.data.count || 0);
          if (detailLink) {
            const updated = (fresh.data.results || []).find(r => r.id === detailLink.id);
            if (updated) setDetailLink(updated);
          }
        }
      } catch (_) {
        fetchPaymentLinks(currentPage, pageSize, search);
      }
    }
  }, [pendingProofLink, pendingProof, pendingAction, reviewNotes, search, currentPage, pageSize, detailLink, fetchPaymentLinks]);

  // Load existing payments for the attach modal (searchable)
  const loadPaymentsForAttach = useCallback(async (searchVal = "") => {
    setPaymentSearchLoading(true);
    try {
      const response = await actions.loadAllPayments(searchVal, 1, 20);
      if (response.status === 200) {
        const rows = response.data.results || response.data || [];
        setPaymentOptions(rows.map((p) => ({
          value: p.id,
          label: `${p.receipt_no || p.id.slice(0, 8)} · ${formatMoney(p.original_amount ?? p.amount, p.currency_code || 'INR')}${p.transaction_ref ? ` · ${p.transaction_ref}` : ''}${p.status ? ` · ${p.status}` : ''}`,
        })));
      }
    } catch (e) {
      console.error("Error loading payments:", e);
    }
    setPaymentSearchLoading(false);
  }, []);

  const openAttachModal = (record) => {
    setAttachingLink(record);
    setSelectedPaymentId(null);
    setPaymentOptions([]);
    setAttachModalOpen(true);
    loadPaymentsForAttach();
  };

  const handleAttachPayment = async () => {
    if (!attachingLink || !selectedPaymentId) {
      message.warning("Select a payment to attach");
      return;
    }
    setAttachLoading(true);
    try {
      const response = await actions.attachPaymentToLink(attachingLink.id, selectedPaymentId);
      if (response.status === 200) {
        message.success("Payment attached to link");
        setAttachModalOpen(false);
        setAttachingLink(null);
        setSelectedPaymentId(null);
        fetchPaymentLinks(currentPage, pageSize, search);
      }
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to attach payment");
      console.error(e);
    }
    setAttachLoading(false);
  };

  // Resend confirmation email (only when link has an SMTP config + template).
  // Pass a proofId to resend for a single submitted payment.
  const handleResendEmail = async (record, proofId = null) => {
    message.loading({ content: 'Sending confirmation email...', key: 'resendEmail' });
    try {
      const response = await actions.resendPaymentLinkEmail(record.id, proofId);
      if (response.status === 200) {
        message.success({
          content: `Confirmation email sent${response.data?.recipient ? ` to ${response.data.recipient}` : ''}`,
          key: 'resendEmail',
        });
      }
    } catch (e) {
      message.error({
        content: e?.response?.data?.error || "Failed to send confirmation email",
        key: 'resendEmail',
      });
      console.error(e);
    }
  };

  // Action menu items
  const getActionItems = (record) => {
    const items = [
      {
        key: "details",
        label: "View Details",
        icon: <EyeOutlined />,
        onClick: () => openDetailModal(record),
      },
      {
        key: "copy",
        label: "Copy Link",
        icon: <CopyOutlined />,
        onClick: () => handleCopyLink(record.payment_link_url),
      },
      {
        key: "clone",
        label: "Clone Link",
        icon: <BlockOutlined />,
        onClick: () => handleCloneLink(record),
      },
    ];

    // No account attached yet — offer the customer-details collection link
    if (!record.account_name && record.customer_details_url) {
      items.push({
        key: "copy_details_link",
        label: "Copy Customer Details Link",
        icon: <CopyOutlined />,
        onClick: () => handleCopyCustomerDetailsLink(record.customer_details_url),
      });
    }

    // Opens the payments list filtered to this link. Always offered — an empty
    // result is itself the answer ("nothing has been collected yet").
    items.push({
      key: "go_to_payments",
      label: "Go to Payments",
      icon: <DollarOutlined />,
      onClick: () => navigate(`/payments?payment_link=${record.id}`),
    });

    if (getDbStatus(record) !== "CANCELLED") {
      items.push({
        key: "attach_payment",
        label: "Attach Payment",
        icon: <PaperClipOutlined />,
        onClick: () => openAttachModal(record),
      });
    }

    // Resend confirmation email — only when an SMTP config is set on the link
    if (record.confirmation_smtp_config) {
      items.push({
        key: "resend_email",
        label: "Resend Confirmation Email",
        icon: <MailOutlined />,
        onClick: () => handleResendEmail(record),
      });
    }

    if (record.is_manual_payment_pending) {
      items.push({
        key: "review",
        label: "Review Manual Payment",
        icon: <CheckCircleOutlined />,
        onClick: () => openReviewModal(record),
      });
    }

    if (getDbStatus(record) === "ACTIVE" && !record.is_manual_payment_pending) {
      items.push({
        key: "approve",
        label: "Approve Payment",
        icon: <CheckCircleOutlined />,
        onClick: () => handleApprovePayment(record),
      });
    }

    if (isEditable(record)) {
      items.push({
        key: "edit",
        label: "Edit",
        icon: <EditOutlined />,
        onClick: () => openEditModal(record),
      });
    }

    if (getDbStatus(record) === "ACTIVE") {
      items.push({
        key: "cancel",
        label: "Cancel",
        icon: <StopOutlined />,
        danger: true,
        onClick: () => handleCancel(record),
      });
    }

    return items;
  };

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (_, record) => {
        const status = getEffectiveStatus(record);
        return (
          <Space size={4}>
            <Tag color={statusColors[status] || "default"}>{status}</Tag>
            {record.is_manual_payment_pending && (
              <Tag color="orange">UPI</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: "Account",
      dataIndex: "account_name",
      key: "account_name",
      ellipsis: true,
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
      ellipsis: true,
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      align: "right",
      render: (amount, record) => amount ? formatMoney(amount, recordCurrency(record)) : "-",
    },
    {
      title: "Payments",
      key: "received_count",
      width: 170,
      align: "center",
      render: (_, record) => {
        const count = record.received_count ?? 0;
        const pending = (record.proofs_summary || []).filter(p => p.status === 'PENDING').length;
        if (count === 0 && pending === 0) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Tooltip title="View these payments">
            <div
              style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); navigate(`/payments?payment_link=${record.id}`); }}
            >
              {count > 0 && <Tag color="green" style={{ marginInlineEnd: 0, whiteSpace: 'nowrap' }}>{count} received</Tag>}
              {pending > 0 && <Tag color="orange" style={{ marginInlineEnd: 0, whiteSpace: 'nowrap' }}>{pending} pending</Tag>}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Invoice",
      dataIndex: "invoice_data",
      key: "invoice_data",
      width: 160,
      render: (invoiceData) => {
        if (!invoiceData) return <Text type="secondary">-</Text>;
        const statusColor = {
          DRAFT: "default", SENT: "blue", PARTIAL: "orange",
          PAID: "green", READY: "cyan", CANCELLED: "red",
        };
        return (
          <Tooltip
            title={
              <div>
                <div><strong>{invoiceData.inv_number}</strong></div>
                <div>Status: {invoiceData.status}</div>
                {invoiceData.account_name && <div>Account: {invoiceData.account_name}</div>}
                {invoiceData.total != null && <div>Total: {formatMoney(invoiceData.total, invoiceData.currency_code)}</div>}
                {invoiceData.balance != null && <div>Balance: {formatMoney(invoiceData.balance, invoiceData.currency_code)}</div>}
                {invoiceData.invoice_date && <div>Date: {dayjs(invoiceData.invoice_date).format("DD MMM YYYY")}</div>}
              </div>
            }
          >
            <Space size={4}>
              <FileTextOutlined style={{ color: '#1677ff' }} />
              <Text style={{ fontSize: 12 }}>{invoiceData.inv_number}</Text>
              <Tag color={statusColor[invoiceData.status] || "default"} style={{ fontSize: 11, marginRight: 0 }}>
                {invoiceData.status}
              </Tag>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      render: (date) => date ? dayjs(date).format("DD MMM YYYY") : "-",
    },
    {
      title: "Expires",
      dataIndex: "expires_at",
      key: "expires_at",
      width: 150,
      render: (date) => date ? dayjs(date).format("DD MMM YYYY HH:mm") : "-",
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            menu={{ items: getActionItems(record) }}
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "0 24px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, marginTop: 16 }}>
        <FilterBar
          searchValue={searchLive}
          onSearchChange={handleSearch}
          onSearch={handleSearch}
          searchPlaceholder="Search payment links"
        />
        <PageActions
          primary={{
            label: "Create Payment Link",
            shortLabel: "Create",
            icon: <PlusOutlined />,
            onClick: () => openCreateModal(),
          }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} payment links`,
          onChange: (page, size) => {
            // A new page size starts again from page 1.
            setCurrentPage(size !== pageSize ? 1 : page);
            setPageSize(size);
          },
        }}
        onRow={(record) => ({
          onClick: () => openDetailModal(record),
          style: { cursor: "pointer" },
        })}
        size="middle"
        sticky
      />

      {/* Create Payment Link Modal */}
      <Modal
        title={createResult
          ? "Payment Link Created"
          : (createPrefill ? "Clone Payment Link" : "Create Payment Link")}
        open={createModalOpen}
        onCancel={closeCreateModal}
        footer={createResult ? [
          <Button key="close" onClick={closeCreateModal}>
            Close
          </Button>,
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={() => handleCopyLink(createResult.payment_link_url)}>
            Copy Link
          </Button>,
        ] : null}
        width={createResult ? 520 : 720}
        destroyOnClose
      >
        {createResult ? (
          <div>
            <p style={{ marginBottom: 12, color: '#52c41a', fontWeight: 500 }}>Payment link created successfully!</p>
            <Input
              value={createResult.payment_link_url}
              readOnly
              addonAfter={
                <CopyOutlined onClick={() => handleCopyLink(createResult.payment_link_url)} style={{ cursor: 'pointer' }} />
              }
            />
            <div style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
              Amount: {formatMoney(createResult.amount, recordCurrency(createResult))} | Expires: {dayjs(createResult.expires_at).format("YYYY-MM-DD HH:mm")}
            </div>
          </div>
        ) : (
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreatePaymentLink}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="currency"
                  label="Currency"
                  tooltip="The customer is charged in this currency. Foreign currencies need a gateway with that currency enabled (international payments)."
                >
                  <Select
                    showSearch
                    placeholder="Select currency"
                    optionFilterProp="label"
                    options={currencies.map((c) => ({
                      value: c.id,
                      label: `${c.iso_code} — ${c.currency}`,
                    }))}
                    onChange={(value) => {
                      const iso = currencies.find((c) => c.id === value)?.iso_code;
                      // Manual UPI is INR-only.
                      if (iso && iso !== 'INR') {
                        createForm.setFieldsValue({ is_manual_payment_allowed: false });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Amount is required' }]}>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    precision={currencyDecimals(createCurrencyIso)}
                    placeholder="Enter amount"
                    prefix={currencyPrefix(createCurrencyIso)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="description" label="Description">
                  <Input placeholder="Payment description (optional)" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="account" label="Account">
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select account (optional)"
                    filterOption={false}
                    onSearch={(val) => loadAccounts(val)}
                    options={accountOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="product"
                  label="Product"
                  tooltip="Tag this payment link to a Teson product (e.g., SignVerse, PhotoStore)."
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select product (optional)"
                    optionFilterProp="label"
                    options={productOptions}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="external_reference_id"
                  label="Reference ID"
                  tooltip="External system reference (e.g. subscription UUID, invoice number from another system)."
                >
                  <Input placeholder="External reference ID (optional)" maxLength={255} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="customer_name" label="Customer Name">
                  <Input placeholder="Customer name (optional)" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="customer_email" label="Customer Email">
                  <Input placeholder="Customer email (optional)" type="email" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="customer_phone" label="Customer Phone">
                  <Input placeholder="Customer phone (optional)" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="redirect_url"
                  label="Redirect URL"
                  tooltip="After a successful payment, the customer will be redirected to this URL."
                  rules={[{ type: 'url', message: 'Enter a valid URL (including https://)' }]}
                >
                  <Input placeholder="https://example.com/thank-you (optional)" type="url" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="is_manual_payment_allowed"
                  label="Allow manual UPI payment"
                  tooltip="When ON, customer sees the company UPI ID, QR code, and screenshot upload on the public page. INR links only."
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch disabled={createCurrencyIso !== 'INR'} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="multiple_payments"
                  label="Accept multiple payments"
                  tooltip="When ON, this link can collect unlimited payments until it expires. Each payer must provide their own name, email, and phone."
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="send_confirmation_email"
                  label="Send confirmation email"
                  tooltip="When ON, the payer receives the chosen template via the chosen SMTP after a successful payment."
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch />
                </Form.Item>
              </Col>
              {createSendEmailOn && (
                <>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="confirmation_smtp_config"
                      label="SMTP configuration"
                      tooltip="SMTP account used to send the confirmation email."
                      rules={[{ required: true, message: 'Select an SMTP configuration' }]}
                    >
                      <Select
                        allowClear
                        showSearch
                        placeholder="Select SMTP config"
                        optionFilterProp="label"
                        options={smtpOptions}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="confirmation_email_template"
                      label="Confirmation email template"
                      tooltip="Email template rendered with payer/payment context."
                      rules={[{ required: true, message: 'Select an email template' }]}
                    >
                      <Select
                        allowClear
                        showSearch
                        placeholder="Select email template"
                        optionFilterProp="label"
                        options={templateOptions}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
            <Form.Item
              name="banner_image"
              label="Banner image (optional)"
              tooltip="Image shown at the top of the public payment page (e.g., a poster with payment details)."
              valuePropName="file"
              getValueFromEvent={(e) => e?.fileList?.[0]?.originFileObj || null}
            >
              <Upload
                accept="image/*"
                beforeUpload={() => false}
                maxCount={1}
                listType="picture"
                fileList={createBannerFileList}
                onChange={(e) => setCreateBannerFileList((e.fileList || []).slice(-1))}
              >
                <Button icon={<UploadOutlined />}>Choose image</Button>
              </Upload>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={createLoading} block>
                {createPrefill ? "Create Cloned Link" : "Create Payment Link"}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Edit Payment Link Modal — only opens for ACTIVE + not-expired links */}
      <Modal
        title="Edit Payment Link"
        open={editModalOpen}
        onCancel={() => { setEditModalOpen(false); setEditingLink(null); }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditPaymentLink}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="amount" label="Amount">
                <InputNumber
                  style={{ width: '100%' }}
                  precision={currencyDecimals(recordCurrency(editingLink))}
                  prefix={currencyPrefix(recordCurrency(editingLink))}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="description" label="Description">
                <Input placeholder="Payment description (optional)" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="account" label="Account">
                <Select
                  allowClear showSearch placeholder="Select account (optional)"
                  filterOption={false} options={accountOptions} disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="product" label="Product">
                <Select
                  allowClear showSearch placeholder="Select product (optional)"
                  optionFilterProp="label" options={productOptions} disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="external_reference_id" label="Reference ID">
                <Input placeholder="External reference ID (optional)" maxLength={255} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="customer_name" label="Customer Name">
                <Input placeholder="Customer name (optional)" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="customer_email" label="Customer Email">
                <Input placeholder="Customer email (optional)" type="email" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="customer_phone" label="Customer Phone">
                <Input placeholder="Customer phone (optional)" disabled />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="redirect_url" label="Redirect URL"
                rules={[{ type: 'url', message: 'Enter a valid URL (including https://)' }]}
              >
                <Input placeholder="https://example.com/thank-you (optional)" type="url" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="is_manual_payment_allowed" label="Allow manual UPI payment" valuePropName="checked">
                <Switch disabled={recordCurrency(editingLink) !== 'INR'} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="multiple_payments" label="Accept multiple payments" valuePropName="checked">
                <Switch disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="send_confirmation_email" label="Send confirmation email" valuePropName="checked">
                <Switch disabled />
              </Form.Item>
            </Col>
            {editSendEmailOn && (
              <>
                <Col xs={24} sm={12}>
                  <Form.Item name="confirmation_smtp_config" label="SMTP configuration">
                    <Select allowClear showSearch placeholder="Select SMTP config" optionFilterProp="label" options={smtpOptions} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="confirmation_email_template" label="Confirmation email template">
                    <Select allowClear showSearch placeholder="Select email template" optionFilterProp="label" options={templateOptions} disabled />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
          {editingLink?.banner_image && (
            <Form.Item label="Current banner image">
              <img
                src={editingLink.banner_image}
                alt="Current banner"
                style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, border: '1px solid #f0f0f0' }}
              />
            </Form.Item>
          )}
          <Form.Item
            name="banner_image"
            label={editingLink?.banner_image ? 'Replace banner image (optional)' : 'Banner image (optional)'}
            tooltip="Image shown at the top of the public payment page (e.g., a poster with payment details)."
            valuePropName="file"
            getValueFromEvent={(e) => e?.fileList?.[0]?.originFileObj || null}
          >
            <Upload accept="image/*" beforeUpload={() => false} maxCount={1} listType="picture">
              <Button icon={<UploadOutlined />}>{editingLink?.banner_image ? 'Replace image' : 'Choose image'}</Button>
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={editLoading} block>
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Manual Payment Review Modal */}
      <Modal
        title={`Review Manual Payment`}
        open={reviewModalOpen}
        onCancel={() => { setReviewModalOpen(false); setReviewingLink(null); }}
        footer={null}
        width={600}
      >
        {reviewingLink && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Text type="secondary">Amount:</Text>{' '}
              <Text strong>{formatMoney(reviewingLink.amount, recordCurrency(reviewingLink))}</Text>
              {reviewingLink.customer_name && (
                <>
                  {' | '}
                  <Text type="secondary">Customer:</Text>{' '}
                  <Text>{reviewingLink.customer_name}</Text>
                </>
              )}
              {reviewingLink.description && (
                <>
                  {' | '}
                  <Text type="secondary">{reviewingLink.description}</Text>
                </>
              )}
            </div>
            {reviewingLink.manual_payment_screenshot && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>Payment Screenshot</div>
                <Image
                  src={reviewingLink.manual_payment_screenshot}
                  alt="Payment Screenshot"
                  style={{ maxHeight: 300, objectFit: 'contain', borderRadius: 8, border: '1px solid #d9d9d9' }}
                />
              </div>
            )}
            {reviewingLink.manual_payment_reference && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>Reference / UTR</div>
                <div style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '8px 12px', borderRadius: 6 }}>
                  {reviewingLink.manual_payment_reference}
                </div>
              </div>
            )}
            {reviewingLink.manual_payment_submitted_at && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>Submitted On</div>
                <div>{dayjs(reviewingLink.manual_payment_submitted_at).format('DD MMM YYYY, hh:mm A')}</div>
              </div>
            )}
            <div>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>Notes</div>
              <Input.TextArea
                rows={2}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add a note (optional)"
              />
            </div>
            <Flex gap={12} justify="flex-end">
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => initiateReviewAction('reject')}
              >
                Reject
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => initiateReviewAction('approve')}
                style={{ background: '#16A34A', borderColor: '#16A34A' }}
              >
                Approve Payment
              </Button>
            </Flex>
          </div>
        )}
      </Modal>

      {/* View Details Modal */}
      <Modal
        title="Payment Link Details"
        open={detailModalOpen}
        onCancel={() => { setDetailModalOpen(false); setDetailLink(null); }}
        footer={[
          <Button
            key="go_to_payments"
            icon={<DollarOutlined />}
            onClick={() => navigate(`/payments?payment_link=${detailLink?.id}`)}
          >
            Go to Payments
          </Button>,
          <Button key="copy" icon={<CopyOutlined />} onClick={() => handleCopyLink(detailLink?.payment_link_url)}>
            Copy Link
          </Button>,
          <Button key="close" type="primary" onClick={() => { setDetailModalOpen(false); setDetailLink(null); }}>
            Close
          </Button>,
        ]}
        width={980}
        style={{ top: 24 }}
        styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      >
        {detailLink && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                <div><Tag color={statusColors[getEffectiveStatus(detailLink)]}>{getEffectiveStatus(detailLink)}</Tag></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Amount</Text>
                <div><Text strong>{formatMoney(detailLink.amount, recordCurrency(detailLink))}</Text></div>
                {/* Foreign-currency payment: show the INR value it was recorded at. */}
                {detailLink.payment_data && detailLink.payment_data.currency_code !== 'INR' && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {detailLink.payment_data.exchange_rate != null
                        ? `≈ ${formatMoney(detailLink.payment_data.amount_inr, 'INR')} @ ${detailLink.payment_data.exchange_rate}`
                        : 'INR value pending: exchange rate unavailable'}
                    </Text>
                  </div>
                )}
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Description</Text>
                <div><Text>{detailLink.description || '-'}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Account</Text>
                <div><Text>{detailLink.account_name || '-'}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Customer Name</Text>
                <div><Text>{detailLink.customer_name || '-'}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Customer Email</Text>
                <div><Text>{detailLink.customer_email || '-'}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Customer Phone</Text>
                <div><Text>{detailLink.customer_phone || '-'}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Created By</Text>
                <div><Text>{detailLink.created_by_name || '-'}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Created At</Text>
                <div><Text>{detailLink.created_at ? dayjs(detailLink.created_at).format('DD MMM YYYY, hh:mm A') : '-'}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Expires At</Text>
                <div><Text>{detailLink.expires_at ? dayjs(detailLink.expires_at).format('DD MMM YYYY, hh:mm A') : '-'}</Text></div>
              </div>
              {detailLink.redirect_url && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Redirect URL</Text>
                  <div style={{ wordBreak: 'break-all' }}>
                    <a href={detailLink.redirect_url} target="_blank" rel="noopener noreferrer">{detailLink.redirect_url}</a>
                  </div>
                </div>
              )}
              {detailLink.product_name && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Product</Text>
                  <div>
                    <Text>{detailLink.product_name}</Text>
                    {detailLink.product_code && (
                      <Text type="secondary"> ({detailLink.product_code})</Text>
                    )}
                  </div>
                </div>
              )}
              {detailLink.external_reference_id && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Reference ID</Text>
                  <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {detailLink.external_reference_id}
                  </div>
                </div>
              )}
            </div>
            {getDbStatus(detailLink) !== "CANCELLED" && (
              <Flex justify="flex-end">
                <Button
                  icon={<PaperClipOutlined />}
                  onClick={() => { setDetailModalOpen(false); openAttachModal(detailLink); }}
                >
                  Attach Payment
                </Button>
              </Flex>
            )}
            {detailLink.multiple_payments && (
              <div style={{ marginTop: 8, padding: '12px 16px', background: '#f9f9f9', border: '1px solid #e5e5e5', borderRadius: 8 }}>
                <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 13 }}>Submitted payments</Text>
                  <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {detailLink.received_count ?? 0} confirmed
                    </Text>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      disabled={(detailLink.proofs_summary || []).length === 0}
                      onClick={() => handleExportSubmittedPayments(detailLink)}
                    >
                      Export
                    </Button>
                  </Space>
                </Flex>
                {(detailLink.proofs_summary || []).length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>No submissions yet.</Text>
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={detailLink.proofs_summary}
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: 'Receipt',
                        key: 'receipt',
                        render: (_, p) => (
                          p.payment ? (
                            <a
                              onClick={() => navigate(`/payments/edit/${p.payment}/`)}
                              style={{ fontFamily: 'monospace', fontSize: 13 }}
                            >
                              {p.payment_receipt_no || p.payment}
                            </a>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
                          )
                        ),
                      },
                      {
                        title: 'Customer Name',
                        dataIndex: 'customer_name',
                        key: 'customer_name',
                        render: (v) => v ? <Text strong>{v}</Text> : <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
                      },
                      {
                        title: 'Email',
                        dataIndex: 'customer_email',
                        key: 'customer_email',
                        render: (v) => v || <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
                      },
                      {
                        title: 'Phone',
                        dataIndex: 'customer_phone',
                        key: 'customer_phone',
                        render: (v) => v || <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
                      },
                      {
                        title: 'UTR / Ref',
                        dataIndex: 'reference',
                        key: 'reference',
                        render: (v) => v ? <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> : <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
                      },
                      {
                        title: 'Screenshot',
                        dataIndex: 'screenshot_url',
                        key: 'screenshot_url',
                        width: 90,
                        align: 'center',
                        render: (url) => url ? (
                          <Image
                            src={url}
                            alt="Payment screenshot"
                            width={44}
                            height={44}
                            style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                          />
                        ) : <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
                      },
                      {
                        title: 'Submitted At',
                        dataIndex: 'submitted_at',
                        key: 'submitted_at',
                        render: (v) => v ? <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD MMM YYYY, hh:mm A')}</Text> : <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
                      },
                      {
                        title: 'Payment Date',
                        dataIndex: 'payment_date',
                        key: 'payment_date',
                        align: 'right',
                        render: (v) => v ? <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD MMM YYYY')}</Text> : <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
                      },
                      {
                        title: 'Amount Received',
                        dataIndex: 'amount',
                        key: 'amount',
                        align: 'right',
                        render: (v) => v != null ? <Text strong>{formatMoney(v, recordCurrency(detailLink))}</Text> : <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
                      },
                      {
                        title: 'Payment Mode',
                        dataIndex: 'payment_mode',
                        key: 'payment_mode',
                        render: (v) => v ? <Tag>{v}</Tag> : <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
                      },
                      {
                        title: 'Linked',
                        key: 'is_linked',
                        align: 'center',
                        render: (_, p) => (
                          p.is_linked ? (
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
                          )
                        ),
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        render: (s) => (
                          <Tag color={s === 'CONFIRMED' ? 'green' : s === 'PENDING' ? 'orange' : 'red'} style={{ marginRight: 0 }}>
                            {s}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        fixed: 'right',
                        width: 70,
                        align: 'center',
                        render: (_, p) => {
                          const actionItems = [];
                          if (p.status === 'PENDING') {
                            actionItems.push({
                              key: 'approve',
                              label: 'Approve',
                              icon: <CheckCircleOutlined />,
                              onClick: () => initiateProofAction(detailLink, p, 'approve'),
                            });
                            actionItems.push({
                              key: 'reject',
                              label: 'Reject',
                              icon: <CloseCircleOutlined />,
                              danger: true,
                              onClick: () => initiateProofAction(detailLink, p, 'reject'),
                            });
                          } else if (p.status === 'CONFIRMED' && detailLink.confirmation_smtp_config) {
                            actionItems.push({
                              key: 'resend',
                              label: 'Resend Email',
                              icon: <MailOutlined />,
                              onClick: () => handleResendEmail(detailLink, p.id),
                            });
                          }

                          // Payment exists but no account attached yet — copy this
                          // payment's own customer-details form link to send to the payer.
                          if (p.payment && !p.is_linked && p.customer_details_url) {
                            actionItems.push({
                              key: 'copy_details_link',
                              label: 'Copy Customer Details Link',
                              icon: <CopyOutlined />,
                              onClick: () => handleCopyCustomerDetailsLink(p.customer_details_url),
                            });
                          }

                          if (actionItems.length === 0) {
                            return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>;
                          }

                          return (
                            <Dropdown menu={{ items: actionItems }} trigger={['click']}>
                              <Button type="text" icon={<MoreOutlined />} />
                            </Dropdown>
                          );
                        },
                      },
                    ]}
                  />
                )}
              </div>
            )}
            {detailLink.invoice_data ? (
              <div style={{ marginTop: 8, padding: '12px 16px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8 }}>
                <Text strong style={{ fontSize: 13 }}><FileTextOutlined /> Linked Invoice</Text>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginTop: 8 }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Invoice Number</Text>
                    <div><Text strong>{detailLink.invoice_data.inv_number}</Text></div>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                    <div>
                      <Tag color={{ DRAFT: "default", SENT: "blue", PARTIAL: "orange", PAID: "green", READY: "cyan", CANCELLED: "red" }[detailLink.invoice_data.status] || "default"}>
                        {detailLink.invoice_data.status}
                      </Tag>
                    </div>
                  </div>
                  {detailLink.invoice_data.account_name && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>Account</Text>
                      <div><Text>{detailLink.invoice_data.account_name}</Text></div>
                    </div>
                  )}
                  {detailLink.invoice_data.total != null && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>Total</Text>
                      <div><Text>{formatMoney(detailLink.invoice_data.total, detailLink.invoice_data.currency_code)}</Text></div>
                    </div>
                  )}
                  {detailLink.invoice_data.balance != null && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>Balance</Text>
                      <div><Text>{formatMoney(detailLink.invoice_data.balance, detailLink.invoice_data.currency_code)}</Text></div>
                    </div>
                  )}
                  {detailLink.invoice_data.invoice_date && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>Invoice Date</Text>
                      <div><Text>{dayjs(detailLink.invoice_data.invoice_date).format('DD MMM YYYY')}</Text></div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 8, padding: '12px 16px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 8 }}>
                <Text strong style={{ fontSize: 13 }}><FileTextOutlined /> Invoice (To be generated)</Text>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginTop: 8 }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Amount</Text>
                    <div><Text strong>{formatMoney(detailLink.amount, recordCurrency(detailLink))}</Text></div>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                    <div><Tag color="default">NOT CREATED</Tag></div>
                  </div>
                  {detailLink.account_name && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>Account</Text>
                      <div><Text>{detailLink.account_name}</Text></div>
                    </div>
                  )}
                  {detailLink.description && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>Description</Text>
                      <div><Text>{detailLink.description}</Text></div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {detailLink.banner_image && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Banner Image</Text>
                <div style={{ marginTop: 4 }}>
                  <Image
                    src={detailLink.banner_image}
                    alt="Banner"
                    style={{ maxHeight: 200, objectFit: 'contain', borderRadius: 8, border: '1px solid #d9d9d9' }}
                  />
                </div>
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Payment Link URL</Text>
              <div style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '8px 12px', borderRadius: 6, wordBreak: 'break-all', fontSize: 13 }}>
                {detailLink.payment_link_url || '-'}
              </div>
            </div>
            {detailLink.manual_payment_screenshot && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Payment Screenshot</Text>
                <div style={{ marginTop: 4 }}>
                  <Image
                    src={detailLink.manual_payment_screenshot}
                    alt="Payment Screenshot"
                    style={{ maxHeight: 200, objectFit: 'contain', borderRadius: 8, border: '1px solid #d9d9d9' }}
                  />
                </div>
              </div>
            )}
            {detailLink.manual_payment_reference && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Reference / UTR</Text>
                <div style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '8px 12px', borderRadius: 6 }}>
                  {detailLink.manual_payment_reference}
                </div>
              </div>
            )}
            {detailLink.manual_payment_notes && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Notes</Text>
                <div style={{ background: '#f5f5f5', padding: '8px 12px', borderRadius: 6 }}>
                  {detailLink.manual_payment_notes}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Attach Existing Payment Modal */}
      <Modal
        title="Attach Payment to Link"
        open={attachModalOpen}
        onCancel={() => { setAttachModalOpen(false); setAttachingLink(null); setSelectedPaymentId(null); }}
        onOk={handleAttachPayment}
        okText="Attach Payment"
        okButtonProps={{ disabled: !selectedPaymentId, loading: attachLoading }}
        destroyOnClose
      >
        {attachingLink && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
            <div>
              <Text type="secondary">Link amount:</Text>{' '}
              <Text strong>{formatMoney(attachingLink.amount || 0, recordCurrency(attachingLink))}</Text>
              {attachingLink.customer_name && (
                <>{' | '}<Text type="secondary">Customer:</Text> <Text>{attachingLink.customer_name}</Text></>
              )}
            </div>
            <div>
              <div style={{ fontWeight: 500, marginBottom: 6 }}>Select an existing payment</div>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search by receipt no, reference, amount..."
                filterOption={false}
                loading={paymentSearchLoading}
                onSearch={(val) => loadPaymentsForAttach(val)}
                onChange={(val) => setSelectedPaymentId(val)}
                value={selectedPaymentId}
                options={paymentOptions}
                notFoundContent={paymentSearchLoading ? "Loading..." : "No payments found"}
              />
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {attachingLink.multiple_payments
                ? "The selected payment will be attached to this link. The link stays active so more payments can be attached."
                : "The selected payment will be attached and this link will be marked as PAID."}
            </Text>
          </div>
        )}
      </Modal>

      {/* Confirm Action Modal with random code */}
      <Modal
        title={`Confirm ${(pendingAction === 'reject' || pendingAction === 'proof_reject') ? 'Rejection' : 'Approval'}`}
        open={confirmModalVisible}
        onCancel={() => {
          setConfirmModalVisible(false);
          setPendingAction(null);
          setPendingProof(null);
          setPendingProofLink(null);
        }}
        onOk={
          pendingAction === 'direct_approve'
            ? handleConfirmDirectApprove
            : (pendingAction === 'proof_approve' || pendingAction === 'proof_reject')
              ? handleConfirmProofAction
              : handleConfirmReviewAction
        }
        okText={(pendingAction === 'reject' || pendingAction === 'proof_reject') ? 'Reject' : 'Approve'}
        okButtonProps={{
          disabled: userInputCode !== confirmCode,
          loading: reviewLoading,
          danger: pendingAction === 'reject' || pendingAction === 'proof_reject',
          style: !(pendingAction === 'reject' || pendingAction === 'proof_reject')
            ? { background: userInputCode === confirmCode ? '#16A34A' : undefined, borderColor: userInputCode === confirmCode ? '#16A34A' : undefined }
            : {},
        }}
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Text>
            {pendingAction === 'reject'
              ? 'This will reject the manual payment. The customer can resubmit.'
              : pendingAction === 'proof_reject'
                ? `This will reject ${pendingProof?.customer_name || 'this'}'s payment proof. They can submit another one.`
                : pendingAction === 'proof_approve'
                  ? `This will approve ${pendingProof?.customer_name || 'this'}'s payment proof and create a payment record. The link will remain active.`
                  : 'This will mark the payment as approved and create a payment record.'}
          </Text>
          {(pendingAction === 'direct_approve' || pendingAction === 'proof_approve' || pendingAction === 'proof_reject') && (
            <div style={{ marginTop: 12 }}>
              <Input.TextArea
                rows={2}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add a note (optional)"
              />
            </div>
          )}
          <div style={{ margin: '20px 0' }}>
            <Text strong>Type the code below to confirm:</Text>
            <div style={{
              fontSize: '28px',
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '6px',
              color: pendingAction === 'reject' ? '#ff4d4f' : '#16A34A',
              background: pendingAction === 'reject' ? '#fff2f0' : '#f6ffed',
              padding: '12px 24px',
              borderRadius: '8px',
              display: 'inline-block',
              margin: '12px 0',
              userSelect: 'none',
            }}>
              {confirmCode}
            </div>
          </div>
          <Input
            placeholder="Enter the code above"
            value={userInputCode}
            onChange={(e) => setUserInputCode(e.target.value.toUpperCase())}
            style={{ textAlign: 'center', fontSize: '18px', fontFamily: 'monospace', letterSpacing: '4px' }}
            maxLength={5}
            onPressEnter={() => {
              if (userInputCode === confirmCode) {
                pendingAction === 'direct_approve' ? handleConfirmDirectApprove() : handleConfirmReviewAction();
              }
            }}
          />
          {userInputCode && userInputCode !== confirmCode && (
            <Text type="danger" style={{ display: 'block', marginTop: 8 }}>Code does not match</Text>
          )}
          {userInputCode === confirmCode && (
            <Text type="success" style={{ display: 'block', marginTop: 8 }}>Code matched!</Text>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PaymentLinksTable;
